import "./styles.css";
import Container from "../../../components/Container";
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Table, Button, Input, Space } from "antd";
import { db } from "../../../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import {
  EyeOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import NaoConformidadeModal from "../../../components/NaoConformidadeModal";

function ClientDashboard() {
  const [certificados, setCertificados] = useState([]);
  const [certificadosFilter, setCertificadosFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuario, setUsuario] = useState({});
  const [ncModal, setNcModal] = useState({ visible: false, certificado: null });

  // Estatísticas do cliente
  const [stats, setStats] = useState({
    totalValvulas: 0,
    totalCertificados: 0,
  });

  useEffect(() => {
    const usuarioData = JSON.parse(sessionStorage.getItem("seepilUsuario"));
    setUsuario(usuarioData || {});
  }, []);

  const handleSearch = useCallback(
    (search, key) => {
      const searchTerm = search.toLowerCase();
      const data = certificados.filter((item) => {
        const value = item[key];
        return value?.toString().toLowerCase().includes(searchTerm);
      });
      setCertificadosFilter(search ? data : certificados);
    },
    [certificados]
  );

  const reset = useCallback(() => {
    setCertificadosFilter(certificados);
  }, [certificados]);

  const columns = [
    {
      title: "Ações",
      dataIndex: "id",
      key: "actions",
      width: 100,
      fixed: "left",
      render: (value, record) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
          <Link
            to={`/cliente/cadastro-certificados/${value}`}
            className="view-btn"
          >
            <EyeOutlined />
          </Link>
          <button
            className="view-btn nc-flag-btn"
            title="Reportar não conformidade"
            onClick={() => setNcModal({ visible: true, certificado: record })}
          >
            <WarningOutlined />
          </button>
        </div>
      ),
    },
    {
      title: "TAG",
      dataIndex: "tag",
      key: "tag",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Tag"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "tag");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#e6be01" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Nº Série",
      dataIndex: "numeroSerie",
      key: "numeroSerie",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº Série"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "numeroSerie");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#e6be01" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidadeCliente",
      key: "unidadeCliente",
    },
    {
      title: "Tipo de Válvula",
      dataIndex: "tipoValvula",
      key: "tipoValvula",
    },
    {
      title: "Tipo de Certificado",
      dataIndex: "tipoCertificado",
      key: "tipoCertificado",
    },
    {
      title: "Nº Certificado",
      dataIndex: "numeroCertificado",
      key: "numeroCertificado",
    },
    {
      title: "SPL",
      dataIndex: "spl",
      key: "spl",
    },
  ];

  const fetchClientsFirebase = useCallback(async () => {
    const snapshot = await getDocs(collection(db, "clientes"));
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return data;
  }, []);

  const fetchFromCollection = async (collectionName, tipoColecao) => {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      tipoColecao,
      ...doc.data(),
    }));
  };

  const fetchData = useCallback(async () => {
    try {
      const seepilUsuario = JSON.parse(sessionStorage.getItem("seepilUsuario"));
      const clienteId = seepilUsuario?.cliente_id;

      const unidadesPermitidas =
        Array.isArray(seepilUsuario?.unidadesCliente) &&
        seepilUsuario.unidadesCliente.length > 0
          ? seepilUsuario.unidadesCliente
          : seepilUsuario?.unidadeCliente
          ? [seepilUsuario.unidadeCliente]
          : [];

      if (!clienteId || unidadesPermitidas.length === 0) {
        setCertificados([]);
        setCertificadosFilter([]);
        setStats({ totalValvulas: 0, totalCertificados: 0 });
        setLoading(false);
        return;
      }

      // Buscar certificados
      const certificadosSnapshot = await getDocs(collection(db, "certificados"));
      const certificadosList = certificadosSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filtrar certificados do cliente
      const filteredCertificados = certificadosList.filter(
        (item) =>
          item.cliente_id === clienteId &&
          unidadesPermitidas.includes(item.unidadeCliente)
      );

      const clients = await fetchClientsFirebase();

      const certificadosWithClient = filteredCertificados.map((cert) => {
        const client = clients.find((c) => c.id === cert.cliente_id);
        return {
          ...client,
          ...cert,
        };
      });

      setCertificados(certificadosWithClient);
      setCertificadosFilter(certificadosWithClient);

      // Buscar válvulas das 3 collections
      const [psv, controle, bloqueio] = await Promise.all([
        fetchFromCollection("valvulasPSV", "PSV"),
        fetchFromCollection("valvulasControle", "Controle"),
        fetchFromCollection("valvulasBloqueio", "Bloqueio"),
      ]);

      const allValves = [...psv, ...controle, ...bloqueio];

      // Filtrar válvulas do cliente
      const filteredValves = allValves.filter(
        (item) =>
          item.cliente_id === clienteId &&
          unidadesPermitidas.includes(item.unidadeCliente)
      );

      // Atualizar estatísticas
      setStats({
        totalValvulas: filteredValves.length,
        totalCertificados: filteredCertificados.length,
      });

      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      setLoading(false);
    }
  }, [fetchClientsFirebase]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
    <Container userType="cliente">
      <div className="dashboard-container">
        {/* Card de Boas-vindas */}
        <div className="welcome-card">
          <div className="welcome-content">
            <h1 className="welcome-title">Bem-vindo ao Seepil Valve Care</h1>
            <p className="welcome-subtitle">
              <span className="welcome-name">
                {usuario.nomeUsuario || "Usuário"}
              </span>
              <span className="welcome-divider">•</span>
              <span className="welcome-company">
                {usuario.nomeEmpresa || "Empresa"}
              </span>
            </p>
          </div>
          <div className="welcome-decoration">
            <div className="decor-circle-1"></div>
            <div className="decor-circle-2"></div>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="stats-grid">
          <Link to="/cliente/valvulas" className="stat-card">
            <div className="stat-icon">
              <SettingOutlined />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.totalValvulas}</span>
              <span className="stat-label">Válvulas Cadastradas</span>
            </div>
          </Link>

          <Link to="/cliente/certificados" className="stat-card">
            <div className="stat-icon">
              <SafetyCertificateOutlined />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.totalCertificados}</span>
              <span className="stat-label">Certificados Ativos</span>
            </div>
          </Link>
        </div>

        {/* Tabela de Certificados */}
        <div className="table-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <h2 className="card-title">Certificados Recentes</h2>
                <p className="card-subtitle">
                  Acesse seus certificados através da tabela abaixo ou pelo menu lateral.
                </p>
              </div>
            </div>
            <Link to="/cliente/certificados" className="view-all-btn">
              Ver todos
            </Link>
          </div>

          <Table
            dataSource={certificadosFilter}
            columns={columns}
            bordered
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
            className="custom-table"
          />
        </div>
      </div>
    </Container>

    <NaoConformidadeModal
      visible={ncModal.visible}
      certificado={ncModal.certificado}
      onClose={() => setNcModal({ visible: false, certificado: null })}
    />
    </>
  );
}

export default ClientDashboard;
